import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { TokenService } from '@core/services/token.service';
import { AuthService } from '@core/services/auth.service';
import { ToastService } from '@core/services/toast.service';
import {
  MODULE_NOT_IN_PLAN_MESSAGE,
  isModuleNotInPlan,
} from '@core/utils/error.util';

/** Endpoints that must NOT carry a Bearer token or trigger a refresh loop. */
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register-school',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
];

// Module-scoped refresh coordination (the interceptor fn is a singleton).
let isRefreshing = false;
const refreshedToken$ = new BehaviorSubject<string | null>(null);

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some((e) => url.includes(e));
}

function withToken(
  req: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Attaches the Bearer access token, and on a 401 performs a single silent
 * refresh while queueing any concurrent requests until the new token arrives,
 * then retries them. If the refresh itself fails, the user is logged out.
 * A 403 MODULE_NOT_IN_PLAN (plan-gated module) shows a friendly toast.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const toast = inject(ToastService);

  // Never attach tokens to (or refresh on) the public auth endpoints.
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  const access = tokenService.getAccessToken();
  const authReq = access ? withToken(req, access) : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // An expired impersonation token can't be refreshed — end impersonation
        // and let the super_admin fall back to their own session.
        if (tokenService.isImpersonating()) {
          tokenService.clearImpersonationToken();
          window.location.assign('/platform/dashboard');
          return throwError(() => error);
        }
        if (tokenService.getRefreshToken()) {
          return handle401(req, next, authService);
        }
      }
      // A module outside the school's plan — explain it once, then let the
      // caller handle the error as usual.
      if (isModuleNotInPlan(error)) {
        toast.error(MODULE_NOT_IN_PLAN_MESSAGE);
      }
      return throwError(() => error);
    }),
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
): Observable<HttpEvent<unknown>> {
  if (isRefreshing) {
    // A refresh is already in flight — queue this request until it completes.
    return refreshedToken$.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap((token) => next(withToken(req, token))),
    );
  }

  isRefreshing = true;
  refreshedToken$.next(null);

  return authService.refresh().pipe(
    switchMap((tokens) => {
      isRefreshing = false;
      refreshedToken$.next(tokens.accessToken);
      return next(withToken(req, tokens.accessToken));
    }),
    catchError((error: unknown) => {
      isRefreshing = false;
      authService.forceLogout();
      return throwError(() => error);
    }),
  );
}
